import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useCart } from "../context/CartContext";
import { useCartDrawer } from "../context/CartDrawerContext";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";

const CartDrawer = () => {
  const { isOpen, closeCart } = useCartDrawer();
  const { user } = useAuth();
  const { publicAuth, bearerAuth } = useApi();
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY || "";
  const {
    cart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    getSubtotal,
  } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zipcode: "",
  });

  useEffect(() => {
    if (!isOpen || !user) return;
    setForm((prev) => ({
      ...prev,
      contact_name: prev.contact_name || user.full_name || user.username || "",
      contact_phone: prev.contact_phone || String(user.contact || ""),
      contact_email: prev.contact_email || user.email || "",
      city: prev.city || user.city || "",
      zipcode: prev.zipcode || String(user.zipcode || ""),
    }));
  }, [isOpen, user]);

  const paymentRequest = (url, payload) => {
    if (user) return bearerAuth(url, "POST", payload);
    return publicAuth(url, "POST", payload);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Unable to load Razorpay checkout."));
      document.body.appendChild(script);
    });

  const startProductPayment = async (payload) => {
    await loadRazorpayScript();
    const orderRes = await paymentRequest("product/payments/razorpay/order", payload);
    const order = orderRes?.data?.data;
    if (!order?.order_id) {
      throw new Error(orderRes?.data?.message || "Unable to create payment order.");
    }

    const checkoutKey = order.key || razorpayKey;
    if (!checkoutKey) {
      throw new Error("Razorpay key is not configured.");
    }

    return new Promise((resolve, reject) => {
      const razorpay = new window.Razorpay({
        key: checkoutKey,
        amount: order.amount_paisa,
        currency: order.currency || "INR",
        name: "Asysta",
        description: "Product Payment",
        order_id: order.order_id,
        prefill: {
          name: payload.contact_name,
          email: payload.contact_email,
          contact: payload.contact_phone,
        },
        handler: async (response) => {
          try {
            const verifyRes = await paymentRequest("product/payments/razorpay/verify", {
              items: payload.items,
              payment_key: payload.payment_key,
              contact_email: payload.contact_email,
              contact_phone: payload.contact_phone,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const verified = verifyRes?.data?.data;
            if (!verifyRes?.data?.status || !verified?.payment_token) {
              throw new Error(verifyRes?.data?.message || "Payment verification failed.");
            }
            resolve(verified);
          } catch (err) {
            reject(new Error(err?.response?.data?.message || err?.message || "Payment verification failed."));
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment was cancelled.")),
        },
        theme: { color: "#284eb8" },
      });
      razorpay.on("payment.failed", (response) => {
        reject(new Error(response?.error?.description || "Payment failed."));
      });
      razorpay.open();
    });
  };

  const handleCheckout = async () => {
    if (!cart.length || checkoutLoading) return;
    const required = ["contact_name", "contact_phone", "contact_email", "address_line1", "city", "state", "zipcode"];
    const missing = required.find((key) => !String(form[key] || "").trim());
    if (missing) {
      window.alert("Please fill contact and delivery address details.");
      setCheckoutOpen(true);
      return;
    }

    try {
      setCheckoutLoading(true);
      const payload = {
        items: cart.map((item) => ({ product_id: item.id, quantity: item.quantity })),
        payment_key: `product_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        ...form,
      };
      const payment = await startProductPayment(payload);
      const response = await paymentRequest("product/book", {
        ...payload,
        payment_token: payment.payment_token,
      });
      if (!response?.data?.status) {
        throw new Error(response?.data?.message || "Product booking failed.");
      }
      clearCart();
      setCheckoutOpen(false);
      closeCart();
      window.alert("Payment successful. Product booking confirmed and receipt sent to email.");
    } catch (err) {
      window.alert(err?.message || "Checkout failed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeCart]);

  // Prevent scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [isOpen]);

  const overlayRef = useRef(null);
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) closeCart();
  };

  if (typeof document === "undefined") return null; // SSR safety

  return createPortal(
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className={`cd-overlay ${isOpen ? "show" : ""}`}
        aria-hidden={!isOpen}
      />

      {/* Drawer */}
      <aside
        className={`cd-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        <header className="cd-header">
          <h3>🛍️ Your Cart</h3>
          <button className="cd-close" onClick={closeCart} aria-label="Close cart">×</button>
        </header>

        <section className="cd-body">
          {cart.length === 0 ? (
            <p className="cd-empty">Your cart is empty</p>
          ) : (
            cart.map((item) => (
              <div key={item.id} className="cd-item">
                <div className="cd-item-info">
                  <strong className="cd-title">{item.title}</strong>
                  <div className="cd-meta">
                    <span>{item.currency || "INR"} {Number(item.price || 0).toFixed(2)}</span>
                    {typeof item.stock !== "undefined" && (
                      <small> • Stock: {item.stock}</small>
                    )}
                  </div>
                </div>
                <div className="cd-qty">
                  <button onClick={() => decreaseQuantity(item.id)} aria-label="Decrease">−</button>
                  <span aria-live="polite">{item.quantity}</span>
                  <button onClick={() => increaseQuantity(item.id)} aria-label="Increase">+</button>
                </div>
                <button className="cd-remove" onClick={() => removeFromCart(item.id)}>Remove</button>
              </div>
            ))
          )}
        </section>

        {cart.length > 0 && (
          <footer className="cd-footer">
            {checkoutOpen && (
              <div className="cd-checkout-form">
                <input placeholder="Full name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                <input placeholder="Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} />
                <input placeholder="Email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                <input placeholder="Address line 1" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} />
                <input placeholder="Address line 2" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
                <div className="cd-form-row">
                  <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                  <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                </div>
                <input placeholder="Zipcode" value={form.zipcode} onChange={(e) => setForm({ ...form, zipcode: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
              </div>
            )}
            <div className="cd-subtotal">
              Subtotal:
              <b>
                {" "}{cart[0]?.currency || "INR"} {getSubtotal().toFixed(2)}
              </b>
            </div>
            <div className="cd-actions">
              <button className="cd-clear" onClick={clearCart}>Clear Cart</button>
              <button className="cd-checkout" onClick={checkoutOpen ? handleCheckout : () => setCheckoutOpen(true)} disabled={checkoutLoading}>
                {checkoutLoading ? "Processing..." : checkoutOpen ? "Pay & Book" : "Checkout"}
              </button>
            </div>
          </footer>
        )}
      </aside>

      <style>{`
        .cd-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.35);
          opacity: 0; pointer-events: none;
          transition: opacity .25s ease;
          z-index: 9998;
        }
        .cd-overlay.show { opacity: 1; pointer-events: auto; }

        .cd-drawer {
          position: fixed; top: 0; right: -420px; height: 100vh; width: 380px;
          max-width: calc(100vw - 48px);
          background: #fff; box-shadow: -2px 0 20px rgba(0,0,0,.15);
          display: flex; flex-direction: column;
          transition: right .3s ease;
          z-index: 9999;
        }
        .cd-drawer.open { right: 0; }

        .cd-header {
          padding: 16px 16px; background: #f8f9fa;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid #eee;
        }
        .cd-close {
          background: transparent; border: none; font-size: 28px; line-height: 1;
          cursor: pointer;
        }

        .cd-body { padding: 12px 16px; overflow-y: auto; flex: 1; }
        .cd-empty { text-align: center; color: #666; padding: 24px 0; }

        .cd-item {
          display: grid; grid-template-columns: 1fr auto auto; gap: 8px 12px;
          align-items: center; padding: 12px 0; border-bottom: 1px solid #f0f0f0;
        }
        .cd-item-info { min-width: 0; }
        .cd-title { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cd-meta { color: #555; font-size: .9rem; }

        .cd-qty {
          display: inline-flex; align-items: center; gap: 6px;
          border: 1px solid #ddd; border-radius: 6px; padding: 2px 6px;
          height: 32px;
        }
        .cd-qty button {
          background: #fff; border: none; width: 26px; height: 26px; cursor: pointer;
          border-radius: 4px; border: 1px solid #eee;
        }
        .cd-qty span { min-width: 22px; text-align: center; display: inline-block; }

        .cd-remove {
          justify-self: end;
          background: #dc3545; color: #fff; border: none;
          padding: 6px 10px; border-radius: 6px; font-size: .85rem; cursor: pointer;
        }

        .cd-footer {
          border-top: 1px solid #eee; background: #f8f9fa;
          padding: 14px 16px;
        }
        .cd-subtotal { text-align: center; margin-bottom: 10px; }
        .cd-actions { display: grid; gap: 8px; }
        .cd-checkout-form {
          display: grid; gap: 8px; margin-bottom: 12px;
        }
        .cd-checkout-form input {
          width: 100%; border: 1px solid #ddd; border-radius: 6px;
          padding: 9px 10px; font-size: 0.92rem;
        }
        .cd-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .cd-clear, .cd-checkout {
          padding: 10px; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;
        }
        .cd-clear { background: #6c757d; color: #fff; }
        .cd-checkout { background: #007bff; color: #fff; }
      `}</style>
    </>,
    document.body
  );
};

export default CartDrawer;
